import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  TrendingUp, Wallet as WalletIcon, PieChart, User, Grid3X3,
  Gift, Trophy, Users, Shield, Award, Briefcase, X
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

export default function MobileNav({ user }) {
  const { t } = useI18n();
  const NAV_ITEMS = [
    { to: "/trade", label: t("trade"), icon: TrendingUp },
    { to: "/wallet", label: t("wallet"), icon: WalletIcon },
    { to: "/portfolio", label: t("portfolio"), icon: PieChart },
    { to: "/profile", label: t("profile"), icon: User },
  ];
  const MORE_ITEMS = [
    { to: "/referrals", label: t("referrals"), icon: Gift },
    { to: "/leaderboard", label: t("leaderboard"), icon: Trophy },
    { to: "/social", label: t("social"), icon: Users },
    { to: "/tournaments", label: t("tournaments"), icon: Award },
    { to: "/trade", label: t("portfolio"), icon: Briefcase },
  ];
  const loc = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const isActive = (to) => loc.pathname === to;

  if (!user) return null;

  return (
    <>
      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-panel border-t border-line md:hidden safe-bottom"
        style={{ paddingBottom: "calc(var(--safe-bottom) + 4px)" }}>
        <div className="flex items-center justify-around h-[var(--bottom-nav-h)]">
          {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className={`touch-ripple flex flex-col items-center justify-center gap-0.5 touch-target rounded-sm transition-colors ${
                isActive(to)
                  ? "text-buy"
                  : "text-muted hover:text-white"
              }`}
              style={{ minWidth: 0, width: 64 }}
            >
              <Icon className="w-5 h-5" />
              <span className="text-[9px] font-semibold uppercase tracking-wider">
                {label}
              </span>
            </Link>
          ))}
          {/* More button */}
          <button
            onClick={() => setMoreOpen(true)}
            className={`touch-ripple flex flex-col items-center justify-center gap-0.5 touch-target rounded-sm transition-colors ${
              moreOpen ? "text-buy" : "text-muted hover:text-white"
            }`}
            style={{ minWidth: 0, width: 64 }}
          >
            <Grid3X3 className="w-5 h-5" />
            <span className="text-[9px] font-semibold uppercase tracking-wider">{t("more")}</span>
          </button>
        </div>
      </nav>

      {/* More Overlay */}
      {moreOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60" onClick={() => setMoreOpen(false)} />
          {/* Sheet */}
          <div className="absolute bottom-0 left-0 right-0 bg-panel border-t border-line rounded-t-xl sheet-enter safe-bottom"
            style={{ paddingBottom: "calc(var(--safe-bottom) + 16px)" }}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-line">
              <span className="text-xs font-semibold uppercase tracking-widest text-dim">{t("all")}</span>
              <button onClick={() => setMoreOpen(false)} className="p-1 text-muted hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-4 gap-2 p-4">
              {MORE_ITEMS.map(({ to, label, icon: Icon }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setMoreOpen(false)}
                  className={`touch-ripple flex flex-col items-center justify-center gap-1.5 p-3 rounded-sm transition-colors ${
                    isActive(to)
                      ? "text-buy bg-buy-dim"
                      : "text-muted hover:text-white hover:bg-panel-hover"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-[9px] font-semibold uppercase tracking-wider text-center leading-tight">
                    {label}
                  </span>
                </Link>
              ))}
              {/* Admin - only if admin */}
              {user.role === "admin" && (
                <Link
                  to="/admin"
                  onClick={() => setMoreOpen(false)}
                  className={`touch-ripple flex flex-col items-center justify-center gap-1.5 p-3 rounded-sm transition-colors ${
                    isActive("/admin")
                      ? "text-buy bg-buy-dim"
                      : "text-muted hover:text-white hover:bg-panel-hover"
                  }`}
                >
                  <Shield className="w-5 h-5" />
                  <span className="text-[9px] font-semibold uppercase tracking-wider">{t("admin")}</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Spacer for content so bottom nav doesn't overlap */}
      <div className="md:hidden" style={{ height: "calc(var(--bottom-nav-h) + var(--safe-bottom))" }} />
    </>
  );
}
